import {
  ListCard,
  ListCardHeader,
  Badge,
  briefPriorityBadgeVariant,
  briefStatusBadgeVariant,
  formatBriefDate,
  formatBriefPriority,
  formatBriefStatus,
  summarizeBriefSkills,
  cn,
} from '@yeshuman/sdk'
import { type ReactNode } from 'react'
import { Building2, Calendar, MapPin, Users } from 'lucide-react'
import type { Brief } from '../../hooks/use-briefs'

export type BriefListCardProps = {
  brief: Brief
  onOpen?: () => void
  openAriaLabel?: string
  trailingActions?: ReactNode
  variant?: 'default' | 'compact'
  className?: string
}

export function BriefListCard({
  brief,
  onOpen,
  openAriaLabel,
  trailingActions,
  variant = 'default',
  className,
}: BriefListCardProps) {
  const skillSummary = summarizeBriefSkills(brief.skills, { maxNames: variant === 'compact' ? 2 : 3 })
  const clientLabel = brief.client_organisation_name?.trim() || 'Speculative'

  const statusRow = (
    <>
      <Badge variant={briefStatusBadgeVariant(brief.status)}>{formatBriefStatus(brief.status)}</Badge>
      <Badge variant={briefPriorityBadgeVariant(brief.priority)}>{formatBriefPriority(brief.priority)}</Badge>
    </>
  )

  const header = (
    <div className="flex min-w-0 flex-wrap items-start justify-between gap-4">
      <ListCardHeader title={brief.title} />
      <div className="pointer-events-none flex shrink-0 flex-wrap items-center justify-end gap-2">
        {statusRow}
      </div>
    </div>
  )

  const body = (
    <div className="pointer-events-none space-y-2">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Building2 className="size-3.5 shrink-0" aria-hidden />
        <span className="min-w-0 truncate">{clientLabel}</span>
      </div>
      <p className="text-sm text-muted-foreground">{skillSummary.countLine}</p>
      {skillSummary.nameLine ? (
        <p className={cn('text-xs text-muted-foreground', variant === 'compact' && 'line-clamp-1')}>
          {skillSummary.nameLine}
        </p>
      ) : null}
      {variant === 'default' && brief.description?.trim() ? (
        <p className="line-clamp-2 text-sm text-muted-foreground">{brief.description}</p>
      ) : null}
      {brief.location?.trim() ? (
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <MapPin className="size-3 shrink-0" aria-hidden />
          <span className="min-w-0 truncate">{brief.location}</span>
        </div>
      ) : null}
    </div>
  )

  const footerMeta = (
    <div className="flex flex-col gap-2 text-sm text-muted-foreground sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1">
          <Users className="size-3.5" aria-hidden />
          {brief.saved_candidates_count}{' '}
          {brief.saved_candidates_count === 1 ? 'candidate' : 'candidates'}
        </span>
        <span className="inline-flex items-center gap-1">
          <Calendar className="size-3.5" aria-hidden />
          Updated {formatBriefDate(brief.updated_at)}
        </span>
      </div>
      {trailingActions ? (
        <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {trailingActions}
        </div>
      ) : null}
    </div>
  )

  return (
    <ListCard
      className={cn('h-full', className)}
      header={header}
      body={body}
      footer={footerMeta}
      onOpen={onOpen}
      openAriaLabel={openAriaLabel ?? `Open brief ${brief.title}`}
    />
  )
}
